import React, { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Key, Copy, Trash2, Plus, Loader2, CheckCircle, Clock, X } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function TeamInviteCodes() {
  const { team, userRole, canManage, token } = useOutletContext()
  const [codes, setCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [copiedCode, setCopiedCode] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(null)
  
  // Create form state
  const [newCodeForm, setNewCodeForm] = useState({
    role: 'parent',
    expires_days: 30,
    max_uses: null
  })

  useEffect(() => {
    if (team?.team_id && canManage) {
      fetchCodes()
    } else {
      setLoading(false)
    }
  }, [team?.team_id, canManage])

  const fetchCodes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invite-codes/team/${team.team_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })

      if (res.ok) {
        const data = await res.json()
        setCodes(data)
      }
    } catch (err) {
      console.error('Error fetching codes:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    setCreating(true)
    try {
      const res = await fetch(`${API_URL}/api/invite-codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Environment': 'production'
        },
        body: JSON.stringify({
          team_id: team.team_id,
          role: newCodeForm.role,
          expires_days: newCodeForm.expires_days,
          max_uses: newCodeForm.max_uses || null
        })
      })

      if (res.ok) {
        fetchCodes()
        setShowCreateModal(false)
        setNewCodeForm({ role: 'parent', expires_days: 30, max_uses: null })
      } else {
        const error = await res.json()
        alert(error.detail || 'Failed to create invite code')
      }
    } catch (err) {
      console.error('Error creating code:', err)
      alert('An error occurred')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (code) => {
    if (!window.confirm('Are you sure you want to deactivate this invite code?')) return
    
    setDeleteLoading(code)
    try {
      const res = await fetch(`${API_URL}/api/invite-codes/${code}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })

      if (res.ok) {
        fetchCodes()
      } else {
        const error = await res.json()
        alert(error.detail || 'Failed to deactivate code')
      }
    } catch (err) {
      console.error('Error deleting code:', err)
    } finally {
      setDeleteLoading(null)
    }
  }

  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date()
  }

  if (!canManage) {
    return (
      <div className="text-center py-12 text-zinc-400">
        <Key className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
        <p>You don't have permission to manage invite codes</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="team-invite-codes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Invite Codes</h1>
          <p className="text-zinc-400 mt-1">
            Create and manage invite codes for {team?.name}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2.5 bg-black text-white font-medium rounded-xl hover:bg-zinc-700 transition-colors flex items-center gap-2"
          data-testid="create-code-button"
        >
          <Plus className="w-5 h-5" />
          New Code
        </button>
      </div>

      {/* Codes List */}
      {codes.length === 0 ? (
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-8 text-center">
          <Key className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
          <p className="text-zinc-400 font-medium">No invite codes yet</p>
          <p className="text-zinc-500 text-sm mt-1">
            Create an invite code to share with parents and team members.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="invite-codes-list">
          {codes.map((code) => {
            const expired = isExpired(code.expires_at)
            const inactive = !code.is_active
            const disabled = expired || inactive
            
            return (
              <div
                key={code.code}
                className={`bg-zinc-800 rounded-xl border ${disabled ? 'border-zinc-700 opacity-60' : 'border-zinc-700'} p-4`}
                data-testid={`invite-code-${code.code}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-mono font-bold text-white tracking-wider">
                        {code.code}
                      </span>
                      <button
                        onClick={() => copyToClipboard(code.code)}
                        className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
                        title="Copy code"
                        data-testid={`copy-${code.code}`}
                      >
                        {copiedCode === code.code ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-zinc-500" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400 capitalize">
                        {code.role?.replace('_', ' ')}
                      </span>
                      {disabled && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">
                          {inactive ? 'Deactivated' : 'Expired'}
                        </span>
                      )}
                    </div>
                  </div>
                  {!disabled && (
                    <button
                      onClick={() => handleDelete(code.code)}
                      disabled={deleteLoading === code.code}
                      className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Deactivate code"
                      data-testid={`delete-${code.code}`}
                    >
                      {deleteLoading === code.code ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  )}
                </div>
                
                <div className="mt-4 pt-3 border-t border-zinc-700 text-sm text-zinc-400 space-y-1">
                  <div className="flex items-center justify-between">
                    <span>Uses:</span>
                    <span className="font-medium text-zinc-200">
                      {code.uses}{code.max_uses ? ` / ${code.max_uses}` : ''}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Expires:</span>
                    <span className={`font-medium ${expired ? 'text-red-400' : 'text-zinc-200'}`}>
                      {formatDate(code.expires_at)}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div 
            className="fixed inset-0 bg-black/50" 
            onClick={() => setShowCreateModal(false)}
          />
          <div className="relative bg-zinc-800 rounded-2xl shadow-xl w-full max-w-md" data-testid="create-code-modal">
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <h2 className="text-lg font-semibold text-white">Create Invite Code</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
              >
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-zinc-200 mb-2">
                  Default Role
                </label>
                <select
                  value={newCodeForm.role}
                  onChange={(e) => setNewCodeForm({ ...newCodeForm, role: e.target.value })}
                  className="w-full px-3 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none"
                  data-testid="role-select"
                >
                  <option value="parent">Parent</option>
                  <option value="coach">Coach</option>
                  <option value="manager">Manager</option>
                  <option value="volunteer">Volunteer</option>
                </select>
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-medium text-zinc-200 mb-2">
                  Valid For
                </label>
                <select
                  value={newCodeForm.expires_days}
                  onChange={(e) => setNewCodeForm({ ...newCodeForm, expires_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none"
                  data-testid="expires-select"
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>

              {/* Max Uses */}
              <div>
                <label className="block text-sm font-medium text-zinc-200 mb-2">
                  Max Uses <span className="text-zinc-500 font-normal">(optional)</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={newCodeForm.max_uses || ''}
                  onChange={(e) => setNewCodeForm({ 
                    ...newCodeForm, 
                    max_uses: e.target.value ? parseInt(e.target.value) : null 
                  })}
                  placeholder="Unlimited"
                  className="w-full px-3 py-2.5 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none"
                  data-testid="max-uses-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Leave empty for unlimited uses
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t border-zinc-700">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-2.5 border border-zinc-700 text-zinc-200 font-medium rounded-xl hover:bg-zinc-900 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 py-2.5 bg-black text-white font-medium rounded-xl hover:bg-zinc-700 disabled:bg-zinc-600 transition-colors flex items-center justify-center gap-2"
                data-testid="confirm-create-button"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Code'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
